import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Settings,
  Tag,
  Users,
  LogOut,
  Store,
  Menu,
  Layers,
  Image as ImageIcon,
  Ticket,
  BarChart3,
  Sparkles,
  Undo2,
  Banknote,
  Megaphone,
  Clapperboard,
  Palette,
  Bell,
  CreditCard,
  Globe,
  Sliders,
  IndianRupee,
  UserCheck,
  Upload,
  Wand2,
  Wallet,
  Rocket,
  Award,
  Flame,
  FileText,
  ExternalLink,
  Gift,
  UserCog,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useBranding, resolveAppDisplayName } from "../../context/BrandingContext";
import { useFeatures } from "../../context/FeaturesContext";
import usePushFeatures from "../../hooks/usePushFeatures";
import NotificationBell from "../common/NotificationBell";
import WalletBadge from "../common/WalletBadge";
import TrialBanner from "../common/TrialBanner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";

const SELLER_NAV = [
  { to: "/seller", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/seller/products", label: "Products", icon: Package },
  { to: "/seller/inventory", label: "Inventory", icon: Layers },
  { to: "/seller/orders", label: "Orders", icon: ShoppingBag },
  { to: "/seller/coupons", label: "Coupons", icon: Ticket },
  { to: "/seller/analytics", label: "Analytics", icon: BarChart3, feature: "advanced_analytics" },
  { to: "/seller/settings", label: "Settings", icon: Settings },
];

const ADMIN_NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/customers", label: "Customers", icon: UserCheck },
  { to: "/admin/staff", label: "Staff & Admins", icon: UserCog },
  { to: "/admin/sellers", label: "Sellers", icon: Store },
  { to: "/admin/categories", label: "Categories", icon: Tag },
  { to: "/admin/brands", label: "Brands", icon: Award },
  { to: "/admin/attributes", label: "Attributes", icon: Layers },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/products/bulk-upload", label: "Bulk Upload", icon: Upload, feature: "bulk_upload" },
  { to: "/admin/products/smart-catalog", label: "Smart Catalog", icon: Wand2, feature: "ai_smart_catalog" },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/returns", label: "Returns", icon: Undo2 },
  { to: "/admin/refunds", label: "Refunds", icon: Banknote },
  { to: "/admin/banners", label: "Banners", icon: ImageIcon },
  { to: "/admin/hero", label: "Homepage Hero", icon: ImageIcon },
  { to: "/admin/home-sections", label: "Home Sections", icon: Sparkles },
  { to: "/admin/sections", label: "Sections", icon: Sparkles },
  { to: "/admin/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/admin/social", label: "Product Reels", icon: Clapperboard, feature: "social_reels" },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket },
  { to: "/admin/loyalty", label: "Loyalty & Rewards", icon: Gift },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, feature: "advanced_analytics" },
  { to: "/admin/branding", label: "Branding", icon: Palette },
  // Visibility follows the per-tenant notification switches (see filter below).
  { to: "/admin/push", label: "Push Broadcasts", icon: Bell },
  // Billing + Upgrade hidden from tenant admins — managed centrally by
  // super admin under /superadmin/tenants. Keep the routes in App.js
  // so super-admins can still impersonate and walk into them if needed,
  // just don't surface them on the sidebar.
  // { to: "/admin/billing", label: "Billing", icon: CreditCard },
  // { to: "/admin/upgrade", label: "Upgrade Plan", icon: Rocket },
  { to: "/admin/wallet", label: "Wallet", icon: Wallet },
  { to: "/admin/domain", label: "Domain & App", icon: Globe },
  { to: "/admin/legal", label: "Legal Pages", icon: FileText },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

const SUPER_NAV = [
  { to: "/superadmin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/superadmin/tenants", label: "Tenants", icon: Users },
  { to: "/superadmin/renewals", label: "Renewals", icon: CreditCard },
  { to: "/superadmin/features", label: "Features", icon: Sliders },
  { to: "/superadmin/plans", label: "Plans", icon: IndianRupee },
  { to: "/superadmin/analytics", label: "Revenue", icon: BarChart3 },
  { to: "/superadmin/wallets", label: "Wallets", icon: Wallet },
  { to: "/superadmin/pricing", label: "Pricing", icon: Tag },
  { to: "/superadmin/firebase", label: "Firebase", icon: Flame },
  { to: "/superadmin/settings", label: "Settings", icon: Settings },
];

function SidebarBody({ variant, nav, onNavigate }) {
  const { user, seller, logout } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="p-6 border-b border-[hsl(var(--kb-border))] shrink-0">
        <Link to="/" className="font-display text-2xl" onClick={onNavigate}>
          {resolveAppDisplayName(branding)}
        </Link>
        <div className="overline mt-2">
          {variant === "admin" ? "Admin Console" : variant === "super" ? "Super Admin" : "Seller Studio"}
        </div>
        <a
          href="/"
          target="_blank"
          rel="noreferrer"
          onClick={onNavigate}
          className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))] transition-colors"
          data-testid={`${variant}-view-storefront`}
        >
          <ExternalLink size={12} /> View Storefront
        </a>
      </div>
      <nav
        className="flex-1 p-3 overflow-y-auto min-h-0 kb-scroll-hide"
        data-testid={`${variant}-nav-scroll`}
      >
        {nav.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={onNavigate}
              data-testid={`${variant}-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm rounded-sm transition-colors ${
                  isActive
                    ? "bg-[hsl(var(--kb-primary))] text-white"
                    : "text-[hsl(var(--kb-text))] hover:bg-[hsl(var(--kb-surface-alt))]"
                }`
              }
            >
              <Icon size={16} />
              {n.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-4 border-t border-[hsl(var(--kb-border))] text-sm shrink-0">
        <div className="flex items-center gap-2 mb-3">
          <Store size={14} />
          <div className="truncate min-w-0">
            <div className="font-medium truncate">
              {seller?.shop_name || user?.name || user?.phone}
            </div>
            <div className="text-xs text-[hsl(var(--kb-muted))] capitalize">{user?.role}</div>
          </div>
        </div>
        <button
          onClick={() => {
            logout();
            navigate("/");
          }}
          className="flex items-center gap-2 text-xs text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))]"
          data-testid="dashboard-logout"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}

export default function DashboardLayout({ variant, children }) {
  const nav = variant === "admin" ? ADMIN_NAV : variant === "super" ? SUPER_NAV : SELLER_NAV;
  const { has: hasFeature, loading: featsLoading } = useFeatures();
  // Super-admin's per-tenant notification switches: hide the push page when
  // neither Bulk nor Scheduled notifications is enabled for this store.
  const pushFeatures = usePushFeatures({ enabled: variant === "admin" });
  const { branding } = useBranding();
  const isSingleVendor = branding?.business_mode === "single_vendor";
  // Gate nav items by `feature` key. When features haven't loaded yet we
  // optimistically show everything, then filter once the list arrives.
  // Also hide Sellers when the tenant runs in single_vendor mode.
  const filteredNav = (featsLoading
    ? nav
    : nav.filter((n) => !n.feature || hasFeature(n.feature))
  ).filter((n) => !(isSingleVendor && variant === "admin" && n.to === "/admin/sellers"))
    .filter((n) => !(variant === "admin" && n.to === "/admin/push"
      && !pushFeatures.loading && !pushFeatures.anyEnabled));
  const [openMobile, setOpenMobile] = useState(false);

  return (
    <div className="min-h-screen md:grid md:grid-cols-[240px_1fr] bg-[hsl(var(--kb-dashboard-bg))]">
      <aside className="hidden md:flex md:flex-col md:sticky md:top-0 md:h-screen bg-white border-r border-[hsl(var(--kb-border))] safe-top">
        <SidebarBody variant={variant} nav={filteredNav} />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden flex items-center justify-between bg-white border-b border-[hsl(var(--kb-border))] p-4 safe-top sticky top-0 z-30">
        <Link to="/" className="font-display text-xl">{resolveAppDisplayName(branding)}</Link>
        <button
          onClick={() => setOpenMobile(true)}
          className="p-2"
          aria-label="Open navigation"
          data-testid={`${variant}-mobile-nav-btn`}
        >
          <Menu size={20} />
        </button>
      </div>

      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent side="left" className="p-0 w-72">
          <SheetHeader className="sr-only">
            <SheetTitle>Dashboard navigation</SheetTitle>
            <SheetDescription>Switch between sections</SheetDescription>
          </SheetHeader>
          <SidebarBody variant={variant} nav={filteredNav} onNavigate={() => setOpenMobile(false)} />
        </SheetContent>
      </Sheet>

      <main className="p-4 md:p-10 overflow-x-hidden">
        {variant === "admin" && (
          <>
            <TrialBanner />
            <div className="flex justify-end items-center gap-3 mb-2">
              <WalletBadge />
              <NotificationBell />
            </div>
          </>
        )}
        {children}
      </main>
    </div>
  );
}
